import React, { useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDispatch, useSelector, batch } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useTranslation } from 'react-i18next';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';

import { selectLocale } from 'models/localization';
import { UserSessionContext } from 'components/UserSession';
import CircularLoadingIndicator from 'components/indicators/CircularLoadingIndicator';
import {
  fetchContactList,
  selectAllContacts,
  ContactsSlice,
} from 'models/contacts';
import { nameByLocale } from 'hooks/useNameByLocale';
import ContactListItem from './ContactListItem';

const Container = styled.div`
  background-color: #FFF;
  width: 1020px;
  margin: auto;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.16);
  border-radius: 8px;
`;

const StyledTableCell = styled(TableCell)`
  border: none;
  color: '#999998';
  text-transform: capitalize;
`;

const ContactListContainer = ({ scrollableTarget }) => {
  const {
    initialLoading,
    hasNextPage,
  } = useSelector((state) => state.contacts);
  const contacts = useSelector(selectAllContacts);
  const dispatch = useDispatch();
  const { userSession } = useContext(UserSessionContext);
  const { t } = useTranslation();
  const locale = useSelector(selectLocale);

  const fetchMore = useCallback(() => {
    dispatch(fetchContactList(userSession?.rongcloudUserId));
  }, [dispatch]);

  useEffect(() => {
    batch(() => {
      dispatch(ContactsSlice.actions.resetContacts());
      fetchMore();
    });
  }, [dispatch, fetchMore]);

  const getContactRole = (customAttendeeFields) => {
    // eslint-disable-next-line camelcase
    const vexpoOrganizer = customAttendeeFields?.vexpo_organizer;
    // eslint-disable-next-line camelcase
    const vexpoRole = customAttendeeFields?.vexpo_role;
    if (vexpoOrganizer) return 'organizer';
    switch (vexpoRole?.toLowerCase()) {
      case 'organizer':
      case 'exhibitor':
        return vexpoRole?.toLowerCase();
      default:
        return 'attendee';
    }
  };

  const tableHeaderText = [
    t('contact.name'),
    t('contact.email'),
    t('contact.role'),
    t('contact.description'),
    t('contact.date_added'),
  ];

  return (
    <Container>
      {initialLoading && <CircularLoadingIndicator size={32} />}
      {
        !initialLoading && (
          <InfiniteScroll
            dataLength={contacts.length}
            next={fetchMore}
            hasMore={hasNextPage}
            loader={<CircularLoadingIndicator key="loading-indicator" size={32} />}
            scrollableTarget={scrollableTarget}
            scrollThreshold={1}
            style={{ overflow: 'hidden' }}
          >
            <Table aria-label="simple table">
              <TableHead>
                <TableRow>
                  {tableHeaderText.map((header) => (
                    <StyledTableCell key={header}>{header}</StyledTableCell>
                  ))}
                  <StyledTableCell rowSpan={2} />
                </TableRow>
              </TableHead>
              <TableBody>
                {
                  contacts && contacts.map((row) => (
                    <ContactListItem
                      key={row.id}
                      contactId={row.contact.uuid}
                      name={row.contact.firstName && row.contact.lastName ? nameByLocale(row.contact.firstName, row.contact.lastName, locale) : row.contact.name}
                      role={getContactRole(row?.contact?.customAttendeeFields)}
                      email={row.contact.email}
                      createdAt={row.createdAt}
                      description={row.contact.vexpoChatUserDescription}
                    />
                  ))
                }
              </TableBody>
            </Table>
          </InfiniteScroll>
        )
      }
    </Container>
  );
};

ContactListContainer.propTypes = {
  scrollableTarget: PropTypes.instanceOf(Element),
};

ContactListContainer.defaultProps = {
  scrollableTarget: document.body,
};

export default ContactListContainer;
